import queryString from "query-string";
import { ICallableRequestBuilder } from "../requestBuilder/types";
import {
  withAuthenticatedClientRequest,
  withClientRequest,
} from "../requestBuilder/withClientRequest";
import requests from "./requests";
import {
  SignedSaleOrder,
  DutchAuctionOrder,
  ReserveAuctionOrder,
  OfferOrder,
  BaseSignedOrder,
} from "./types";
import config from "@shared/config/client";
import { EnrichedObject, EnrichmentProperties } from "../types";
import { enrichItem, exportAddressesByProperties } from "../helpers";

const createFixedAuction =
  (request: ICallableRequestBuilder<SignedSaleOrder>) =>
  (fixedAuction: SignedSaleOrder) => {
    return request.call(`${config.apiUrl}/orders/fixed-auction`, (init) => ({
      ...init,
      method: "POST",
      headers: {
        ...init.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fixedAuction),
    }));
  };

const createDutchAuction =
  (request: ICallableRequestBuilder<DutchAuctionOrder>) =>
  (dutchAuction: DutchAuctionOrder) => {
    return request.call(`${config.apiUrl}/orders/dutch-auction`, (init) => ({
      ...init,
      method: "POST",
      headers: {
        ...init.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dutchAuction),
    }));
  };

const createReserveAuction =
  (request: ICallableRequestBuilder<ReserveAuctionOrder>) =>
  (reserveAuction: ReserveAuctionOrder) => {
    return request.call(`${config.apiUrl}/orders/reserve-auction`, (init) => ({
      ...init,
      method: "POST",
      headers: {
        ...init.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(reserveAuction),
    }));
  };

const createPrivateAuction =
  (request: ICallableRequestBuilder<SignedSaleOrder>) =>
  (privateAuction: SignedSaleOrder) => {
    return request.call(`${config.apiUrl}/orders/private-auction`, (init) => ({
      ...init,
      method: "POST",
      headers: {
        ...init.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(privateAuction),
    }));
  };

const createOffer =
  (request: ICallableRequestBuilder<OfferOrder>) => (offer: OfferOrder) => {
    return request.call(`${config.apiUrl}/orders/offer`, (init) => ({
      ...init,
      method: "POST",
      headers: {
        ...init.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(offer),
    }));
  };

const placeBid =
  (request: ICallableRequestBuilder<ReserveAuctionOrder>) =>
  (reserveBid: ReserveAuctionOrder) => {
    return request.call(
      `${config.apiUrl}/orders/reserve-auction-bid`,
      (init) => ({
        ...init,
        method: "POST",
        headers: {
          ...init.headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reserveBid),
      }),
    );
  };

const updateAuctionCreateSignature =
  (request: ICallableRequestBuilder<string>) =>
  ({
    orderId,
    signature,
    isDutch,
  }: {
    orderId: string;
    signature: string;
    isDutch?: boolean;
  }) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/orders/auction-signature`,
      query: {
        orderId,
        signature,
        isDutch,
      },
    });

    return request.call(url, (init) => ({
      ...init,
      method: "PUT",
    }));
  };

const updateOfferSignature =
  (request: ICallableRequestBuilder<SignedSaleOrder>) =>
  (orderId: string, signature: string) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/orders/offer-signature`,
      query: {
        orderId,
        signature,
      },
    });

    return request.call(url, (init) => ({
      ...init,
      method: "PUT",
    }));
  };

const updateBidSignature =
  (request: ICallableRequestBuilder<SignedSaleOrder>) =>
  ({
    orderId,
    auctionId,
    signature,
  }: {
    orderId: string;
    auctionId: string;
    signature: string;
  }) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/orders/bid-signature`,
      query: {
        orderId,
        auctionId,
        signature,
      },
    });

    return request.call(url, (init) => ({
      ...init,
      method: "PUT",
    }));
  };

const deleteBid =
  (request: ICallableRequestBuilder<ReserveAuctionOrder>) =>
  (reservationId: string) => {
    return request.call(
      `${config.apiUrl}/orders/bid/${reservationId}`,
      (init) => ({
        ...init,
        method: "DELETE",
      }),
    );
  };

const deleteAuction =
  (request: ICallableRequestBuilder<SignedSaleOrder>) =>
  (auctionId: string) => {
    return request.call(
      `${config.apiUrl}/orders/auction/${auctionId}`,
      (init) => ({
        ...init,
        method: "DELETE",
      }),
    );
  };

const getOrderById =
  (request: ICallableRequestBuilder<any>) => (orderId: string) => {
    return request.call(`${config.apiUrl}/orders/${orderId}`, (init) => ({
      ...init,
      method: "GET",
    }));
  };

const settleReserveAuction =
  (request: ICallableRequestBuilder<string>) =>
  ({ auctionId }: { auctionId: string }) => {
    return request.call(
      `${config.apiUrl}/orders/settle-reserve-auction/${auctionId}`,
      (init) => ({
        ...init,
        method: "POST",
        headers: {
          ...init.headers,
          "Content-Type": "application/json",
        },
      }),
    );
  };

export async function enrichWithOrder<
  T extends { [k: string]: any },
  TPropsToEnrich extends string,
>(array: T[], properties: EnrichmentProperties<T>) {
  const listOfAddresses = exportAddressesByProperties(array, properties);

  if (!listOfAddresses.length) return array;

  const orders = await withClientRequest(requests.getOrderForNfts)(
    listOfAddresses,
  );

  const newArray = array.map((item) => {
    const newItem: any = enrichItem(item, properties, orders, (order) =>
      `${order.nftContract}-${order.tokenId}`.toLowerCase(),
    );

    return newItem;
  });

  return newArray as EnrichedObject<T, TPropsToEnrich, BaseSignedOrder>[];
}

const clientRequests = {
  createFixedAuction: withAuthenticatedClientRequest(createFixedAuction),
  createDutchAuction: withAuthenticatedClientRequest(createDutchAuction),
  createReserveAuction: withAuthenticatedClientRequest(createReserveAuction),
  createPrivateAuction: withAuthenticatedClientRequest(createPrivateAuction),
  createOffer: withAuthenticatedClientRequest(createOffer),
  placeBid: withAuthenticatedClientRequest(placeBid),
  updateAuctionSignature: withAuthenticatedClientRequest(
    updateAuctionCreateSignature,
  ),
  updateBidSignature: withAuthenticatedClientRequest(updateBidSignature),
  updateOfferSignature: withAuthenticatedClientRequest(updateOfferSignature),
  getOrderById: withClientRequest(getOrderById),
  getOrderForNfts: withClientRequest(requests.getOrderForNfts),
  getReserveBidsForNft: withClientRequest(requests.getReserveBidsForNft),
  getOffersForNft: withAuthenticatedClientRequest(requests.getOffersForNft),
  getOrdersForNft: withClientRequest(requests.getOrdersForNft),
  getReceivedOffers: withAuthenticatedClientRequest(requests.getReceivedOffers),
  getMadeOffers: withAuthenticatedClientRequest(requests.getMadeOffers),
  getCollectionOfferForBidder: withAuthenticatedClientRequest(
    requests.getCollectionOfferForBidder,
  ),
  settleReserveAuction: withAuthenticatedClientRequest(settleReserveAuction),
  deleteAuction: withAuthenticatedClientRequest(deleteAuction),
  deleteBid: withAuthenticatedClientRequest(deleteBid),
  calculateNftExchangeFee: withAuthenticatedClientRequest(
    requests.calculateNftExchangeFee,
  ),
  calculateCollectionExchangeFee: withAuthenticatedClientRequest(
    requests.calculateCollectionExchangeFee,
  ),
  enrichWithOrder,
};

export default clientRequests;
